import React, { useState } from 'react';

import { Modal } from '@/components/Modal';
import { Agent } from '@/hooks/useAgents';

import { useTunnelManagement } from './useTunnelManagement';

interface ProvisionTunnelModalProps {
  isOpen: boolean;
  onClose: () => void;
  agent: Agent;
  onSuccess?: (taskId?: string) => void;
}

/**
 * Modal for provisioning a Cloudflare tunnel for an Aegis agent.
 *
 * Extracted from TunnelManagementTab to enable direct modal access from row actions
 * following information architecture best practices (colocation, reusability).
 *
 * Can be opened from:
 * - Row action "Add Tunnel" (direct modal access)
 * - Tunnel tab "Provision Tunnel" button (within drawer)
 */
export const ProvisionTunnelModal: React.FC<ProvisionTunnelModalProps> = ({
  isOpen,
  onClose,
  agent,
  onSuccess,
}) => {
  const [isProvisioning, setIsProvisioning] = useState(false);
  const tunnelManagement = useTunnelManagement(isOpen);

  const handleProvision = async () => {
    if (!agent) return;

    try {
      setIsProvisioning(true);

      const response = await tunnelManagement.createTunnel(agent.client_id);

      onClose();
      onSuccess?.(response?.installTaskId);
    } catch (error) {
      console.error('Failed to provision tunnel:', error);
      // Error handling is managed by the tunnel management hook
    } finally {
      setIsProvisioning(false);
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title="Provision Cloudflare Tunnel"
      footer={{
        text: isProvisioning ? 'Provisioning...' : 'Provision Tunnel',
        onClick: handleProvision,
        disabled: isProvisioning,
      }}
      data-testid="provision-tunnel-modal"
    >
      <div className="flex flex-col gap-4">
        <p className="text-chariot-text-secondary text-sm">
          Provision a Cloudflare tunnel to enable secure SSH access for agent{' '}
          <strong className="text-chariot-text-primary">
            {agent.hostname}
          </strong>{' '}
          without exposing ports to the internet.
        </p>
        <p className="text-chariot-text-secondary text-xs">
          Once provisioned, the tunnel will be configured with Cloudflare Access
          for authentication.
        </p>
      </div>
    </Modal>
  );
};
